import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import {
  calcDateTime,
  DateTimeFormat,
  diffDateTime,
  DurationUnit,
  formatDateTime,
  toUtcDateTime,
} from '@occc-app/lib/date';
import { CircularProgress } from '@occc/ui-design-system';

import { ChartSeries, TimeSeriesChart } from './TimeSeriesChart';

import { formatChartTooltipTitle, getSelectedMetricByType } from '../utils';
import { MetricDataPoint, Metrics, MetricType } from '../types';

type CoreProgramMetricChartProps = {
  metricType: MetricType;
  metrics: Metrics;
  metricsLoading: boolean;
  startDate: Date;
  endDate: Date;
  plotCumulative: boolean;
  interval: DurationUnit;
};

export const CoreProgramMetricChart = ({
  metricType,
  metrics,
  metricsLoading,
  startDate,
  endDate,
  plotCumulative,
  interval,
}: CoreProgramMetricChartProps) => {
  const selectedMetric = useMemo(
    () => getSelectedMetricByType(metricType, metrics),
    [metricType, metrics],
  );

  const formatTooltipTitle = useCallback(
    (utcTime: Date) => formatChartTooltipTitle(interval, metricType, utcTime),
    [interval, metricType],
  );

  const requestedRangeData = selectedMetric.dataPoints.map((dataPoint) => ({
    date: dataPoint.date,
    count: dataPoint.count,
  }));

  const priorRangeData = selectedMetric.dataPoints.map((dataPoint) => ({
    date: dataPoint.date,
    count: dataPoint.priorCount || 0,
  }));

  const constructChartData = (metricCounts: MetricDataPoint[]) =>
    metricCounts.map((point) => ({
      x: toUtcDateTime(point.date),
      y: point.count,
    }));

  const series: ChartSeries[] = [
    {
      key: 'CURRENT_RANGE',
      data: constructChartData(requestedRangeData),
      initialCumulativeValue: selectedMetric.total - selectedMetric.subtotal,
    },
    {
      key: 'REFERENCE_RANGE',
      data: constructChartData(priorRangeData),
      initialCumulativeValue:
        selectedMetric.total - selectedMetric.subtotal - selectedMetric.priorSubtotal,
    },
  ];

  const daysInRange = diffDateTime(endDate, startDate, 'day') + 1;

  const formatChartTooltipSeriesLabel = (key: string, time: Date) => {
    const dateFormat = DateTimeFormat.Date;
    if (key === 'CURRENT_RANGE') {
      return formatDateTime(time, dateFormat);
    }
    return formatDateTime(calcDateTime(time, 'sub', daysInRange, 'day'), dateFormat);
  };

  if (metricsLoading) {
    return (
      <Box
        px={2}
        py={[2, 4]}
        height={300}
        display='flex'
        justifyContent='center'
        alignItems='center'
      >
        <CircularProgress sx={{ m: 'auto' }} />
      </Box>
    );
  }

  return (
    <Box px={2} py={[2, 4]} height={300}>
      <TimeSeriesChart
        plotCumulative={plotCumulative}
        series={series}
        rangeStart={startDate}
        rangeEnd={endDate}
        interval={selectedMetric.interval}
        allowHighlighting={selectedMetric.interval !== 'hour'}
        formatTooltipTitle={formatTooltipTitle}
        formatTooltipSeriesLabel={formatChartTooltipSeriesLabel}
      />
    </Box>
  );
};
